import { WalletAddressType } from "@lib/types/Billing";
import { CountryCodeEnum, CountryEnum } from "@lib/types/Country";
import { SendByteWalletAddressType } from "@lib/types/SendByte";
import { CurrencyEnum } from "@lib/types/Transaction";
import { Tooltip } from "antd";
import { TooltipPlacement } from "antd/lib/tooltip";

export const getStringFromHtml = (htmlContent: string) => {
  return htmlContent.replace(/(<([^>]+)>)/gi, "");
};

export const generateUniqueID = () => {
  return `v1-${Date.now()}-${Math.floor(Math.random() * (9e12 - 1)) + 1e12}`;
};

export const ellipsisLines = (
  content: string,
  placement: TooltipPlacement = "top",
) => {
  if (content) {
    return (
      <Tooltip placement={placement} title={content}>
        <div
          style={{
            display: "block",
            width: "100%",
            maxWidth: 250,
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {content}
          {content.length > 40 && "..."}
        </div>
      </Tooltip>
    );
  } else {
    return handleNullValue(content);
  }
};

export const handleNullValue = (value: string | null) => {
  if (value === null) {
    return "-";
  } else {
    return value;
  }
};

export const digitForCurrency = (
  currency: WalletAddressType | SendByteWalletAddressType | CurrencyEnum,
) => {
  switch (currency) {
    case WalletAddressType.BTC:
      return 8;

    case WalletAddressType.ETH:
      return 16;

    case WalletAddressType.USDT:
      return 6;

    default:
      return 2;
  }
};

export function getSeparators(locale = "tr-TR") {
  const numberWithGroupAndDecimal = 1234567.89;
  const formattedNumber = new Intl.NumberFormat(locale).format(
    numberWithGroupAndDecimal,
  );

  const match = formattedNumber.match(/(\D+)?\d{3}(\D)\d{3}(\D)\d{2}$/);

  if (match) {
    return {
      groupSeparator: match[2],
      decimalSeparator: match[3],
    };
  } else {
    return {
      groupSeparator: ",",
      decimalSeparator: ".",
    };
  }
}

export function currencySymbol(currency: CurrencyEnum) {
  if (!CurrencyEnum[currency]) return null;
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency })
    .formatToParts(0)
    .find((part) => part.type === "currency").value;
}

export const formatCurrency = (
  value: number,
  currency?: WalletAddressType | SendByteWalletAddressType | CurrencyEnum,
) => {
  if (!currency) {
    currency = CurrencyEnum.USD;
  }
  const digit = digitForCurrency(currency);
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: digit,
    maximumFractionDigits: digit,
  }).format(value);
};

export const shortenString = (
  str: string = "",
  maxLength: number = 15,
  cutFromEnd: boolean = false,
) => {
  if (typeof str == "string") {
    str = str?.trim();
  }

  if (str?.length > maxLength) {
    if (cutFromEnd) {
      return str.slice(0, maxLength - 2) + "...";
    }
    const half = (maxLength - 2) / 2;
    return str.slice(0, half) + "..." + str.slice(-half);
  }
  return str || "-";
};

export const numberFormat = (number: number, decimalDigits?: number) => {
  const nFormat = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimalDigits ?? 2,
    maximumFractionDigits: decimalDigits ?? 2,
  });
  return nFormat.format(number);
};

export const showCountryName = (countryCode: CountryEnum): CountryCodeEnum => {
  return CountryCodeEnum[countryCode];
};

export function validateIPaddress(ipaddress: string) {
  if (
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
      ipaddress,
    )
  ) {
    return true;
  }
  return false;
}

export const generateRandomPassword = (length: number = 10): string => {
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const allChars = uppercase + lowercase + numbers;

  let password = "";
  for (let i = 0; i < length; i++) {
    const randomChar = allChars.charAt(
      Math.floor(Math.random() * allChars.length),
    );
    password += randomChar;
  }

  return password;
};

/**
 * Converts a string to a capitalized label format.
 *
 * @param {string} [input=''] - The input string to be converted.
 * @returns {string} The converted string in capitalized label format.
 */
export const convert2label = (input = ""): string => {
  // Define exceptions for words that should not be manipulated
  const exceptions = ["MFA", "C2C", "IP", "EULA", "IB"];

  /**
   * Maps a word to its capitalized form with exceptions.
   *
   * @param {string | undefined} word - The word to be mapped.
   * @returns {string} The capitalized form of the word.
   */
  const mapFn = (word: string | undefined): string => {
    if (!word) return "";
    if (exceptions.includes(word)) return word;
    if (/^[A-Z]+$/.test(word)) return word;
    // Capitalize the first letter and make the rest lowercase
    return word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase();
  };

  let s = input;

  // Replace underscores and hyphens with spaces
  s = s.replace(/[_-]/g, " ");

  // Add a space before capital letters (for camel case and Pascal case)
  s = s.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Iterate through exceptions and add spaces as needed
  // TODO improve performance
  exceptions.forEach((word) => {
    if (s.includes(word)) {
      s = s.replace(new RegExp(word, "g"), () => {
        if (s.startsWith(word)) return `${word} `;
        if (s.endsWith(word)) return ` ${word}`;
        return ` ${word} `;
      });
    }
  });

  // Convert to title case by mapping and joining words
  s = s.split(" ").map(mapFn).join(" ").trim();

  return s;
};
